import cn from 'classnames';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import {
  IoCalendarOutline as CalenderIcon,
  IoCheckmark as CompleteIcon,
  IoPersonOutline as GuestIcon,
  IoTimeOutline as TimeIcon,
  IoPersonAddOutline as GuestAddIcon,
} from 'react-icons/io5';
import { BookitoBookingFormValues } from '../shared/types';
import { BookingStepItem } from './BookingStepItem';
import { BookingStepView, BookingStepViews } from '../shared/constants';
import { SupportedLanguages } from '@carlito/intl';

type Props = {
  isCompleted?: boolean;
  steps: BookingStepView[];
  updateStepState: (param: BookingStepView) => void;
  bookingParams: BookitoBookingFormValues;
  stepState: BookingStepView;
  userLanguage: SupportedLanguages;
  isGuestFormStepValid: boolean;
  skippedStepIds?: string[];
  updateBookingParams: (params: BookitoBookingFormValues) => void;
};

export default function BookingSteps({
  steps,
  updateStepState,
  bookingParams,
  isGuestFormStepValid,
  stepState,
  userLanguage,
  skippedStepIds = [],
  updateBookingParams,
  isCompleted,
}: Props) {
  const currentIndex = useMemo(() => {
    return steps.indexOf(stepState);
  }, [stepState, steps]);

  const onStepClick = (id: BookingStepView) => {
    if (skippedStepIds.includes(id)) return;
    updateStepState(id);
  };

  return (
    <nav
      className={cn(
        'flex rounded-md overflow-hidden  shadow w-full items-center mb-3 text-xs md:text-base',
        isCompleted ? 'bg-primary-default' : 'bg-white'
      )}
      aria-label="Breadcrumb"
    >
      <ol className="flex flex-1">
        {steps.map((step, idx) => {
          if (step === BookingStepViews.Calendar)
            return (
              <BookingStepItem
                key={step}
                onClick={() => {
                  onStepClick(step);
                  updateBookingParams({
                    ...bookingParams,
                    selectedTimeSlot: undefined,
                  });
                }}
                completed={isCompleted || currentIndex > idx}
                isActive={step === stepState}
                value={
                  bookingParams.bookedForDate && (
                    <>
                      {bookingParams.bookedForDate.toLocaleDateString(userLanguage, {
                        month: 'short',
                        day: '2-digit',
                      })}
                    </>
                  )
                }
                icon={<CalenderIcon />}
                title={<Trans>bookito-stepper-form-steps-date</Trans>}
              />
            );

          if (step === BookingStepViews.SlotPicker)
            return (
              <BookingStepItem
                key={step}
                onClick={() => {
                  onStepClick(step);
                }}
                disabled={!bookingParams.bookedForDate}
                completed={isCompleted || currentIndex > idx}
                isActive={step === stepState}
                value={
                  bookingParams.selectedTimeSlot?.startDateTime && (
                    <span>
                      {new Date(bookingParams.selectedTimeSlot.startDateTime).toLocaleTimeString(
                        userLanguage,
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}
                    </span>
                  )
                }
                icon={<TimeIcon />}
                title={<Trans>bookito-stepper-form-steps-time</Trans>}
              />
            );

          if (step === BookingStepViews.GuestForm)
            return (
              <BookingStepItem
                disabled={!bookingParams.selectedTimeSlot?.startDateTime}
                key={step}
                onClick={() => {
                  onStepClick(step);
                }}
                completed={isCompleted || currentIndex > idx}
                isActive={step === stepState}
                value={
                  step === stepState && (
                    <div className={'flex gap-2 items-center'}>
                      <GuestIcon /> <Trans>guest</Trans>
                    </div>
                  )
                }
                icon={<GuestIcon />}
                title={''}
              />
            );

          if (step === BookingStepViews.Extra)
            return (
              <BookingStepItem
                disabled={!isGuestFormStepValid}
                key={step}
                onClick={() => {
                  onStepClick(step);
                }}
                completed={isCompleted || currentIndex > idx}
                isActive={step === stepState}
                value={
                  step === stepState && (
                    <div className={'flex gap-2 items-center'}>
                      <GuestAddIcon /> <Trans>Extras</Trans>
                    </div>
                  )
                }
                icon={<GuestAddIcon />}
                title={''}
              />
            );

          if (step === BookingStepViews.GuestExtra)
            return (
              <BookingStepItem
                disabled={!bookingParams.selectedTimeSlot?.startDateTime}
                key={step}
                onClick={() => {
                  onStepClick(step);
                }}
                completed={isCompleted || currentIndex > idx}
                isActive={step === stepState}
                value={
                  step === stepState && (
                    <div className={'flex gap-2 items-center'}>
                      <GuestIcon /> <Trans>bookito-stepper-form-steps-guests</Trans>{' '}
                    </div>
                  )
                }
                icon={<GuestIcon />}
                title={''}
                // title={<Trans>bookito-stepper-form-steps-guests</Trans>}
              />
            );

          return null;
        })}
      </ol>
      <CompleteIcon
        className={cn('text-2xl w-8 flex-shrink-0 font-extrabold', {
          'opacity-50': currentIndex < steps.length - 1,
          'text-white': isCompleted,
        })}
      />
    </nav>
  );
}
